import { Checkbox, DatePicker, DefaultButton, Dropdown, IPanelStyles, IStackTokens, ITextFieldStyles, mergeStyles, Panel, PanelType, PrimaryButton, ScrollablePane, ScrollbarVisibility, Stack, TextField } from "@fluentui/react";
import React from "react";
import { useDispatch } from "react-redux";
import generalModule from "../../utilities/GeneralModule";
import { LanguageContext } from "../../utilities/LocalizationModule";
import { dataSourceItem } from "../../src/taskpane/redux/models/reduxModels";
import { DatasourceHelper } from "../../src/taskpane/components/helpers/dataSourceHelper";
import { logger } from "../../utilities/logger";
import { DateService } from "../../src/taskpane/services/dateService";

export const scrollStyle = mergeStyles({
    position: "relative",
    height: "calc(100vh - 135px)",
});

const stackTokens: IStackTokens = { childrenGap: 15 };

const panelStyles: Partial<IPanelStyles> = {
    content: {
        paddingLeft: 15,
        paddingRight: 15
    }
}

export type AddDataResponse = {
    name: string;
    title: string;
    type: string;
    required: boolean;
    options: string[];
}

export type AddDataForm = AddDataResponse & {
    value?: string | boolean | number;
    hasError?: boolean;
}

export interface ICMAddDatasourceItemPanelProps {
    panelIsOpen: boolean;
    closePanel: () => void;
    displayName: string;
    dataSourceItem?: dataSourceItem;
    dataSourceHelper?: DatasourceHelper;
}



export const CMAddDatasourceItemPanel = ({ panelIsOpen, closePanel, displayName, dataSourceItem, dataSourceHelper }: ICMAddDatasourceItemPanelProps) => {
    const languageStrings = React.useContext(LanguageContext);

    const [formData, setFormData] = React.useState<AddDataForm[]>([]);

    React.useEffect(() => {
        const setupAsyncWrapper = async () => {
            try {
                const result = await dataSourceHelper.getEditStructure(dataSourceItem);
                setFormData(result.map(item => ({ ...item })))
            } catch (error) {
                logger(error, "CMAddDatasourceItemPanel.tsx (useEffect 1)");
            }
        };
        setupAsyncWrapper();
    }, []);

    const vaildateForm = () => {
        let isValid = true;

        const updatedData = formData.map(item => {
            if (item.required) {
                if (item.value === undefined || item.value === null || item.value === '') {
                    isValid = false;
                    return { ...item, hasError: true };
                }
            }
            return { ...item, hasError: false };
        });

        setFormData(updatedData);

        return isValid;
    }


    const onRenderFooterContent = React.useCallback(
        () => (
            <Stack horizontal horizontalAlign="end" tokens={{ childrenGap: 15 }}>
                <PrimaryButton
                    iconProps={{ iconName: 'Save' }}
                    onClick={() => {
                        const isValid = vaildateForm();

                        if (!isValid) {
                            return;
                        }

                        //Add post logic
                    }}
                    text={languageStrings.Add} />
                <DefaultButton
                    iconProps={{ iconName: 'Checkmark' }}
                    onClick={() => {
                        closePanel();
                    }}
                    text={languageStrings.Close}
                />
            </Stack>
        ),
        [formData],
    );

    return (
        <Panel
            isOpen={panelIsOpen}
            headerText={displayName}
            onDismiss={() => closePanel()}
            isLightDismiss
            closeButtonAriaLabel="Close"
            type={PanelType.custom}
            customWidth={!generalModule.isInConentMangerMode() ? "300px" : "350px"}
            styles={panelStyles}
            onRenderFooterContent={onRenderFooterContent}
            isFooterAtBottom={true}
        >
            <div className={scrollStyle} >
                <ScrollablePane scrollbarVisibility={ScrollbarVisibility.auto}>
                    <form>
                        <Stack tokens={stackTokens} style={{ marginTop: '15px' }}>
                            {formData.map(property => {
                                switch (property.type) {
                                    case 'string':
                                        return <TextField
                                            key={property.name}
                                            label={property.title}
                                            required={property.required}
                                            className="xb-sp-input"
                                            value={formData.find(form => form.name === property.name).value as string}
                                            onChange={(e, newValue) => {
                                                const updatedData = formData.map(form =>
                                                    form.name === property.name
                                                        ? { ...form, value: newValue }
                                                        : form
                                                );
                                                setFormData(updatedData);
                                            }}
                                            errorMessage={property.hasError ? languageStrings.FieldRequired : ""}
                                        />;
                                    case 'number':
                                        return <TextField
                                            key={property.name}
                                            label={property.title}
                                            required={property.required}
                                            type={property.type}
                                            value={formData.find(form => form.name === property.name).value as string}
                                            onChange={(e, newValue) => {
                                                const updatedData = formData.map(form =>
                                                    form.name === property.name
                                                        ? { ...form, value: newValue }
                                                        : form
                                                );
                                                setFormData(updatedData);
                                            }}
                                            className="xb-sp-input"
                                            errorMessage={property.hasError ? languageStrings.FieldRequired : ""}
                                        />;
                                    case 'boolean':
                                        return <Checkbox
                                            key={property.name}
                                            label={property.title}
                                            checked={!!formData.find(form => form.name === property.name).value}
                                            required={property.required}
                                            onChange={(e, newValue) => {
                                                const updatedData = formData.map(form =>
                                                    form.name === property.name
                                                        ? { ...form, value: newValue }
                                                        : form
                                                );
                                                setFormData(updatedData);
                                            }}
                                        />;
                                    case 'datetime':
                                        const dateValue = formData.find(form => form.name === property.name).value as string;
                                        const parsedDate = dateValue ? new Date(dateValue) : undefined;

                                        return (
                                            <div>
                                                <DatePicker
                                                    key={property.name}
                                                    label={property.title}
                                                    isRequired={property.required}
                                                    allowTextInput
                                                    parseDateFromString={DateService.parseDateFromString}
                                                    value={isNaN(parsedDate?.getTime()) ? undefined : parsedDate}
                                                    onSelectDate={(newDate) => {
                                                        const updatedData = formData.map(form =>
                                                            form.name === property.name
                                                                ? { ...form, value: newDate?.toISOString() }  // converting Date object back to string
                                                                : form
                                                        );
                                                        setFormData(updatedData);
                                                    }}
                                                    textField={{
                                                        errorMessage: property.hasError ? languageStrings.FieldRequired : undefined
                                                    }}
                                                />
                                                {/* {property.hasError && <Label styles={{ root: { color: 'red' } }}>{languageStrings.FieldRequired}</Label>} */}
                                            </div>
                                        );
                                    case 'choice':
                                        return (
                                            <Dropdown
                                                key={property.name}
                                                label={property.title}
                                                required={property.required}
                                                className="xb-sp-input"
                                                options={property.options.map(option => ({ key: option, text: option }))}
                                                selectedKey={formData.find(form => form.name === property.name).value as string}
                                                onChange={(event, option, index) => {
                                                    const updatedData = formData.map(form =>
                                                        form.name === property.name
                                                            ? { ...form, value: option?.key }
                                                            : form
                                                    );
                                                    setFormData(updatedData);
                                                }}
                                                errorMessage={property.hasError ? languageStrings.FieldRequired : ""}
                                            />
                                        );
                                    default:
                                        return null;
                                }
                            })}
                        </Stack>
                    </form>
                </ScrollablePane>
            </div>

        </Panel>
    )
};